/**
 * Insights (R249, decisions 1048 to 1050; help `cloud.insights`; docs/specs/cloud/insights.md): production and
 * productivity across jobs, for tenant admins and managers. Every figure comes from `@teslyn/domain` insights.ts, which
 * is the Operations Hub's rules grouped another way. No money figures. A failed read is said in words, never shown as
 * a zero.
 */
import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  INSIGHTS_GRAIN_LABEL,
  INSIGHTS_GROUP_LABEL,
  INSIGHTS_RANGE_LABEL,
  INSIGHTS_SOURCE_LABEL,
  STANDBY_LABEL,
  insightsData,
  insightsDaysOnJob,
  insightsRange,
  insightsRangeIssue,
  insightsSpan,
  insightsView,
  kmText,
  type HistoryJobRow,
  type InsightsData,
  type InsightsGrain,
  type InsightsGroup,
  type InsightsQc,
  type InsightsRangeChoice,
  type InsightsRow,
  type InsightsSourceChoice,
  type InsightsView,
  type OpsRange,
  type StandbyKind,
} from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { SelectField, TextField } from '../components/Fields.tsx';
import { ErrorState, Loading, PageHeader } from '../components/States.tsx';
import { TableWrap } from '../components/TableWrap.tsx';
import { dateText, daysText, hoursText, litresText, localToday } from '../ops/format.ts';
import { loadInsights, removeHistoryJob, type InsightsLoaded } from './api.ts';
import { kmPerHourOneText, kmPerText, percentText, qcText, rateText, aircraftUseText } from './format.ts';
import { HistoryImportPanel } from './HistoryImportPanel.tsx';

export interface InsightsScreenProps {
  readonly sb: SupabaseClient;
  readonly tenantId: string;
  readonly tenantName: string;
  /** The viewer's local date; tests pass a fixed one. */
  readonly today?: string;
}

const RANGES: readonly InsightsRangeChoice[] = ['all', 'this_fy', 'last_fy', 'custom'];
const GROUPS: readonly InsightsGroup[] = ['project', 'aircraft', 'pilot'];
const GRAINS: readonly InsightsGrain[] = ['all', 'week', 'month', 'fy'];
const SOURCES: readonly InsightsSourceChoice[] = ['all', 'teslyn', 'history'];
const STANDBY_ORDER: readonly StandbyKind[] = ['weather', 'diurnal', 'pilot_rdo', 'other', 'unspecified'];

const errorText = (e: unknown): string => (e instanceof Error ? e.message : String(e));

function Tile({
  label,
  value,
  meta,
}: {
  readonly label: string;
  readonly value: string;
  readonly meta?: string;
}): ReactElement {
  return (
    <li className="metric-tile">
      <span className="label muted">{label}</span>
      <span className="metric metric-tile__value">{value}</span>
      {meta && <span className="caption muted">{meta}</span>}
    </li>
  );
}

/** The tile says why there is no rate; the lines QC covered go under a rate that is there. */
function QcTile({ qc }: { readonly qc: InsightsQc }): ReactElement {
  if (qc.state !== 'ran') return <Tile label="QC pass rate" value={qcText(qc)} />;
  return (
    <Tile
      label="QC pass rate"
      value={percentText(qc.pct, 1)}
      meta={`${qc.pass} of ${qc.lines} lines QC ran on, of ${qc.ofLines} flown`}
    />
  );
}

/** The row's name, with what makes it comparable under it. */
function RowName({ row }: { readonly row: InsightsRow }): ReactElement {
  return (
    <th scope="row">
      <span className="data">{row.label}</span>
      {row.history && (
        <>
          {' '}
          <span className="chip chip--brand">History</span>
        </>
      )}
      {row.context && <span className="caption muted"> {row.context}</span>}
    </th>
  );
}

function ProductionTable({
  rows,
  showPeriod,
  group,
}: {
  readonly rows: readonly InsightsRow[];
  readonly showPeriod: boolean;
  readonly group: InsightsGroup;
}): ReactElement {
  return (
    <TableWrap>
      <table className="table" aria-label="Production">
        <thead>
          <tr>
            <th className="label">{INSIGHTS_GROUP_LABEL[group]}</th>
            {showPeriod && <th className="label">Period</th>}
            <th className="label num">Flights</th>
            <th className="label num">Flight hours</th>
            <th className="label num">Survey hours</th>
            <th className="label num">Survey days</th>
            <th className="label num">Km flown</th>
            <th className="label num">Accepted km</th>
            <th className="label num">Per flight</th>
            <th className="label num">Per flight hour</th>
            <th className="label num">Per survey hour</th>
            <th className="label num">Per survey day</th>
            <th className="label num">Fuel used</th>
            {group === 'aircraft' && <th className="label">Use</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key}>
              <RowName row={r} />
              {showPeriod && <td>{r.period.label}</td>}
              <td className="num data">{r.flights}</td>
              <td className="num data">{hoursText(r.flightHours)}</td>
              <td className="num data">{hoursText(r.surveyHours)}</td>
              <td className="num data">{r.surveyDays}</td>
              <td className="num data">{kmText(r.flownKm)}</td>
              <td className="num data">{kmText(r.acceptedKm)}</td>
              <td className="num data">{kmPerText(r.kmPerFlight)}</td>
              <td className="num data">{kmPerHourOneText(r.kmPerFlightHour)}</td>
              <td className="num data">{kmPerHourOneText(r.kmPerSurveyHour)}</td>
              <td className="num data">{kmPerText(r.kmPerSurveyDay)}</td>
              <td className="num data">{litresText(r.fuelUsedL)}</td>
              {group === 'aircraft' && <td>{aircraftUseText(r.use)}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </TableWrap>
  );
}

function QualityTable({
  rows,
  showPeriod,
  group,
}: {
  readonly rows: readonly InsightsRow[];
  readonly showPeriod: boolean;
  readonly group: InsightsGroup;
}): ReactElement {
  return (
    <TableWrap>
      <table className="table" aria-label="Reflys and acceptance">
        <thead>
          <tr>
            <th className="label">{INSIGHTS_GROUP_LABEL[group]}</th>
            {showPeriod && <th className="label">Period</th>}
            <th className="label num">Refly km</th>
            <th className="label num">Refly rate</th>
            <th className="label num">Lines to refly</th>
            <th className="label">First-time acceptance</th>
            <th className="label">QC pass rate</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key}>
              <RowName row={r} />
              {showPeriod && <td>{r.period.label}</td>}
              <td className="num data">{kmText(r.reflyKm)}</td>
              <td className="num data">{percentText(r.reflyPct)}</td>
              <td className="num data">{`${r.reflyLines} of ${r.lineRecords}`}</td>
              <td>{rateText(r.firstTime)}</td>
              <td>{qcText(r.qc)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableWrap>
  );
}

function StandbyTable({
  rows,
  showPeriod,
}: {
  readonly rows: readonly InsightsRow[];
  readonly showPeriod: boolean;
}): ReactElement {
  return (
    <TableWrap>
      <table className="table" aria-label="Standby by cause">
        <thead>
          <tr>
            <th className="label">Project</th>
            {showPeriod && <th className="label">Period</th>}
            {STANDBY_ORDER.map((k) => (
              <th key={k} className="label num">
                {STANDBY_LABEL[k]}
              </th>
            ))}
            <th className="label num">Total standby</th>
            <th className="label num">Maintenance</th>
            <th className="label num">Suspended</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key}>
              <RowName row={r} />
              {showPeriod && <td>{r.period.label}</td>}
              {STANDBY_ORDER.map((k) => (
                <td key={k} className="num data">
                  {daysText(r.standby.byType[k])}
                </td>
              ))}
              <td className="num data">{daysText(r.standby.total)}</td>
              <td className="num data">{daysText(r.maintenanceDays)}</td>
              <td className="num data">{daysText(r.suspendedDays)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableWrap>
  );
}

function ReasonsTable({ view }: { readonly view: InsightsView }): ReactElement {
  if (view.reasons.length === 0) return <p className="body">No lines to refly in this range.</p>;
  return (
    <TableWrap>
      <table className="table" aria-label="Refly reasons">
        <thead>
          <tr>
            <th className="label">Reason</th>
            <th className="label num">Lines</th>
            <th className="label num">Refly km</th>
          </tr>
        </thead>
        <tbody>
          {view.reasons.map((r) => (
            <tr key={r.reason}>
              <th scope="row">{r.reason}</th>
              <td className="num data">{r.lines}</td>
              <td className="num data">{kmText(r.km)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableWrap>
  );
}

function DaysTable({
  data,
  contracts,
  error,
}: {
  readonly data: InsightsData;
  readonly contracts: InsightsLoaded['contracts'];
  readonly error: string | null;
}): ReactElement {
  if (contracts === null) {
    return (
      <p className="notice notice--fail body" role="alert">
        The contracts could not be read, so this table is not shown: {error}
      </p>
    );
  }
  const rows = insightsDaysOnJob(data, contracts);
  if (rows.length === 0) return <p className="body">No job in Teslyn has daily entries yet.</p>;
  return (
    <TableWrap>
      <table className="table" aria-label="Days on the job">
        <thead>
          <tr>
            <th className="label">Project</th>
            <th className="label">Contract</th>
            <th className="label num">Estimated days</th>
            <th className="label num">Days on the job</th>
            <th className="label num">Difference</th>
            <th className="label">Note</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.jobId}>
              <th scope="row" className="data">
                {r.label}
              </th>
              <td className="data">{r.contract ?? '–'}</td>
              <td className="num data">{r.estimatedDays ?? '–'}</td>
              <td className="num data">{r.daysOnJob ?? '–'}</td>
              <td className="num data">
                {r.differenceDays === null ? '–' : `${r.differenceDays > 0 ? '+' : ''}${r.differenceDays}`}
              </td>
              <td>{r.note}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableWrap>
  );
}

function HistoryTable({
  jobs,
  setAside,
  onRemove,
}: {
  readonly jobs: readonly HistoryJobRow[];
  readonly setAside: readonly HistoryJobRow[];
  readonly onRemove: (job: HistoryJobRow) => Promise<void>;
}): ReactElement {
  const [asking, setAsking] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  if (jobs.length === 0) return <p className="body">No old job files are imported.</p>;
  const notCounted = new Set(setAside.map((j) => j.id));
  return (
    <TableWrap>
      <table className="table" aria-label="History jobs">
        <thead>
          <tr>
            <th className="label">Job</th>
            <th className="label">Dates</th>
            <th className="label">File</th>
            <th className="label">Imported</th>
            <th className="label">Counted</th>
            <th className="label">
              <span className="sr-only">Remove</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {jobs.map((j) => (
            <tr key={j.id}>
              <th scope="row">
                <span className="data">{j.number}</span> {j.title}
              </th>
              <td>
                {j.first_date && j.last_date ? `${dateText(j.first_date)} to ${dateText(j.last_date)}` : '–'}
              </td>
              <td className="data">{j.file_name}</td>
              <td>
                {dateText(j.imported_at.slice(0, 10))}
                {j.imported_by_name && <span className="caption muted"> {j.imported_by_name}</span>}
              </td>
              <td>
                {notCounted.has(j.id)
                  ? 'Not counted: a project in Teslyn has this number'
                  : 'Counted in the figures'}
              </td>
              <td>
                {asking === j.id ? (
                  <div className="row">
                    <Button
                      variant="secondary"
                      busy={busy}
                      onClick={() => {
                        setBusy(true);
                        void onRemove(j).finally(() => {
                          setBusy(false);
                          setAsking(null);
                        });
                      }}
                    >
                      {`Remove ${j.number} for good`}
                    </Button>
                    <Button variant="quiet" onClick={() => setAsking(null)}>
                      Keep it
                    </Button>
                  </div>
                ) : (
                  <Button variant="quiet" onClick={() => setAsking(j.id)}>
                    Remove
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableWrap>
  );
}

export function InsightsScreen({
  sb,
  tenantId,
  tenantName,
  today = localToday(),
}: InsightsScreenProps): ReactElement {
  const [loaded, setLoaded] = useState<InsightsLoaded | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [importing, setImporting] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);

  const [choice, setChoice] = useState<InsightsRangeChoice>('all');
  const [custom, setCustom] = useState<OpsRange>({ from: today, to: today });
  const [group, setGroup] = useState<InsightsGroup>('project');
  const [grain, setGrain] = useState<InsightsGrain>('all');
  const [source, setSource] = useState<InsightsSourceChoice>('all');

  const reload = useCallback(() => setAttempt((n) => n + 1), []);
  useEffect(() => {
    let live = true;
    setError(null);
    loadInsights(sb, tenantId)
      .then((l) => live && setLoaded(l))
      .catch((e: unknown) => {
        if (!live) return;
        // figures from an earlier read would pass for today's
        setLoaded(null);
        setError(errorText(e));
      });
    return () => {
      live = false;
    };
  }, [sb, tenantId, attempt]);

  const data = useMemo(() => (loaded ? insightsData(loaded.input) : null), [loaded]);
  const span = useMemo(() => (data ? insightsSpan(data) : null), [data]);
  const range = insightsRange(choice, today, span, custom);
  const issue = insightsRangeIssue(range);
  const view = useMemo(
    () => (data && !issue ? insightsView(data, { group, grain, range, source }) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `range` is a new object each render; its dates are the key
    [data, issue, group, grain, range.from, range.to, source],
  );

  const history = loaded?.input.history ?? [];
  const showPeriod = grain !== 'all';

  return (
    <section className="page ops">
      <PageHeader
        title="Insights"
        subtitle={`Production and productivity across ${tenantName}'s jobs. Quantities only: no money figures.`}
      >
        {loaded && !importing && (
          <Button variant="secondary" onClick={() => setImporting(true)}>
            Import old project files
          </Button>
        )}
      </PageHeader>

      {error && <ErrorState message={error} onRetry={reload} />}
      {!error && !loaded && <Loading what="Insights" />}
      {done && (
        <p className="notice body" role="status">
          {done}
        </p>
      )}

      {loaded && data && (
        <div className="stack ops__figures">
          {importing && (
            <HistoryImportPanel
              sb={sb}
              tenantId={tenantId}
              projectNumbers={loaded.input.projects.map((p) => p.number)}
              historyNumbers={history.map((h) => h.number)}
              onCancel={() => setImporting(false)}
              onDone={(message) => {
                setImporting(false);
                setDone(message);
                reload();
              }}
            />
          )}

          <div className="ops__filters" role="group" aria-label="Filters">
            <SelectField
              label="Range"
              value={choice}
              onChange={(v) => {
                if (v === 'custom' && choice !== 'custom') setCustom(range);
                setChoice(v);
              }}
              options={RANGES.map((r) => [r, INSIGHTS_RANGE_LABEL[r]] as const)}
            />
            {choice === 'custom' && (
              <>
                <TextField
                  label="From"
                  type="date"
                  value={custom.from}
                  onChange={(v) => setCustom({ ...custom, from: v })}
                />
                <TextField
                  label="To"
                  type="date"
                  value={custom.to}
                  onChange={(v) => setCustom({ ...custom, to: v })}
                />
              </>
            )}
            <SelectField
              label="Group by"
              value={group}
              onChange={setGroup}
              options={GROUPS.map((g) => [g, INSIGHTS_GROUP_LABEL[g]] as const)}
            />
            <SelectField
              label="Period"
              value={grain}
              onChange={setGrain}
              options={GRAINS.map((g) => [g, INSIGHTS_GRAIN_LABEL[g]] as const)}
            />
            <SelectField
              label="Jobs"
              value={source}
              onChange={setSource}
              options={SOURCES.map((s) => [s, INSIGHTS_SOURCE_LABEL[s]] as const)}
            />
          </div>
          {issue ? (
            <p className="notice notice--fail body" role="alert">
              {issue}
            </p>
          ) : (
            <p className="caption muted ops__range">{`${dateText(range.from)} to ${dateText(range.to)}`}</p>
          )}
          {loaded.qcError && (
            <p className="notice notice--fail body" role="alert">
              The QC results could not be read, so no QC pass rate is shown: {loaded.qcError}
            </p>
          )}

          {view && (
            <div className="ops__body">
              {view.rows.length === 0 && <p className="body">Nothing was flown or recorded in this range.</p>}
              <ul className="metric-tiles" aria-label="Summary">
                <Tile
                  label="Accepted km"
                  value={kmText(view.total.acceptedKm)}
                  meta={`Flown ${kmText(view.total.flownKm)}`}
                />
                <Tile
                  label="Per survey hour"
                  value={kmPerHourOneText(view.total.kmPerSurveyHour)}
                  meta={`Per flight hour ${kmPerHourOneText(view.total.kmPerFlightHour)}`}
                />
                <Tile
                  label="Refly rate"
                  value={percentText(view.total.reflyPct)}
                  meta={`${kmText(view.total.reflyKm)}, ${view.total.reflyLines} of ${view.total.lineRecords} lines`}
                />
                <Tile
                  label="First-time acceptance"
                  value={percentText(view.total.firstTime.pct, 1)}
                  meta={`${view.total.firstTime.count} of ${view.total.firstTime.of} lines`}
                />
                <QcTile qc={view.total.qc} />
                <Tile
                  label="Fuel used"
                  value={litresText(view.total.fuelUsedL)}
                  meta={`Standby ${daysText(view.total.standby.total)}`}
                />
              </ul>

              <section className="stack" aria-labelledby="insights-production">
                <h2 id="insights-production" className="heading section-title">
                  Production
                </h2>
                <p className="caption muted">
                  Km per hour only compares fairly between like jobs, because line length and turns change it:
                  the aircraft type and line spacing are shown beside each job. “Per” figures are accepted km.
                  {group === 'pilot' &&
                    ' Pilots are listed by name, not ranked. A flight with two pilots counts for each.'}
                  {group === 'aircraft' && ' Use is days, from the aircraft’s first to its last daily entry.'}
                </p>
                {group === 'pilot' && view.noPilotKm > 0 && (
                  <p className="body">{`${kmText(view.noPilotKm)} accepted have no pilot recorded.`}</p>
                )}
                {view.rows.length > 0 && (
                  <ProductionTable rows={view.rows} showPeriod={showPeriod} group={group} />
                )}
              </section>

              <section className="stack" aria-labelledby="insights-quality">
                <h2 id="insights-quality" className="heading section-title">
                  Reflys and acceptance
                </h2>
                <p className="caption muted">
                  First-time acceptance is the reviewed lines accepted in full on their first flight. The QC
                  pass rate counts each line’s newest verdict, passes only, and only lines QC ran on. History
                  jobs have no QC in Teslyn.
                </p>
                {view.rows.length > 0 && (
                  <QualityTable rows={view.rows} showPeriod={showPeriod} group={group} />
                )}
                <h3 className="label">Refly reasons</h3>
                <ReasonsTable view={view} />
              </section>

              <section className="stack" aria-labelledby="insights-standby">
                <h2 id="insights-standby" className="heading section-title">
                  Standby by cause
                </h2>
                {group === 'project' ? (
                  view.rows.length > 0 && <StandbyTable rows={view.rows} showPeriod={showPeriod} />
                ) : (
                  <p className="body">
                    Standby belongs to the project, not an aircraft or a pilot. Group by project to see it.
                  </p>
                )}
              </section>

              <section className="stack" aria-labelledby="insights-days">
                <h2 id="insights-days" className="heading section-title">
                  Days on the job against the contract
                </h2>
                <p className="caption muted">
                  Job to date, whatever the range. Estimated days come from the signed contract; a month is
                  counted as 30 days.
                </p>
                <DaysTable data={data} contracts={loaded.contracts} error={loaded.contractsError} />
              </section>
            </div>
          )}

          <section className="stack" aria-labelledby="insights-history">
            <h2 id="insights-history" className="heading section-title">
              History jobs
            </h2>
            {removeError && (
              <p className="notice notice--fail body" role="alert">
                {removeError}
              </p>
            )}
            <HistoryTable
              jobs={history}
              setAside={data.setAside}
              onRemove={async (job) => {
                setRemoveError(null);
                setDone(null);
                try {
                  await removeHistoryJob(sb, job.id);
                  setDone(`Removed ${job.number}.`);
                  reload();
                } catch (e) {
                  setRemoveError(errorText(e));
                }
              }}
            />
          </section>
        </div>
      )}
    </section>
  );
}
